// src/config/api.js
import axios from "axios";
import { store } from "../redux/store";
import { logout } from "../redux/slices/authSlice";

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "https://hyreback.hyrelancer.com/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Attach token from localStorage or redux store (safe for browser)
api.interceptors.request.use(
  (config) => {
    try {
      if (typeof window !== "undefined") {
        const token = localStorage.getItem("token") || store.getState()?.auth?.token;
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else {
        // server-side: avoid reading localStorage
        const token = store.getState()?.auth?.token;
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (e) {
      console.warn("Error attaching token to request:", e);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Flag to prevent redirect loops
let isRedirecting = false;

// Response interceptor — handles 401, 503, and other errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "";

      // Handle 401 Unauthorized - redirect to unauthorized page
      if (status === 401) {
        if (typeof window !== "undefined" && !isRedirecting) {
          // Check if we're already on an auth/error page
          const isAuthPage = currentPath.includes('/login') ||
            currentPath.includes('/register') ||
            currentPath.includes('/reset-password') ||
            currentPath.includes('/forgot-password') ||
            currentPath === '/unauthorized' ||
            currentPath === '/service-unavailable' ||
            currentPath === '/error';

          // Check if user is actually authenticated (has token in localStorage)
          const hasToken = localStorage.getItem("token");
          const authState = store.getState()?.auth;
          const isAuthenticated = hasToken && authState?.isAuthenticated && authState?.user;

          // Only redirect if:
          // 1. Not already on an auth/error page
          // 2. User has a token (meaning they were logged in)
          // 3. Not already redirecting (prevent loops)
          if (!isAuthPage && hasToken && !isRedirecting) {
            console.warn('🔒 401 Unauthorized detected. User has token but API returned 401. Redirecting to unauthorized page.', {
              path: currentPath,
              hasToken: !!hasToken,
              isAuthenticated,
              endpoint: error.config?.url
            });

            isRedirecting = true;
            store.dispatch(logout());
            localStorage.removeItem("token");

            // Use setTimeout to prevent immediate redirect loops
            setTimeout(() => {
              if (window.location.pathname !== '/unauthorized') {
                window.location.href = "/unauthorized";
              }
              // Reset flag after redirect
              setTimeout(() => {
                isRedirecting = false;
              }, 1000);
            }, 100);
          } else if (!hasToken) {
            // No token means user was never logged in - this is expected for public endpoints
            // Don't redirect, let the component handle it
            console.log('ℹ️ 401 Unauthorized on public endpoint (no token). Not redirecting.');
          }
        }
      }

      // Handle 503 Service Unavailable - redirect to service unavailable page
      if (status === 503) {
        if (typeof window !== "undefined" && currentPath !== '/service-unavailable' && !isRedirecting) {
          isRedirecting = true;
          window.location.href = "/service-unavailable";
          setTimeout(() => {
            isRedirecting = false;
          }, 1000);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Freelancer Job API functions
export const freelancerJobAPI = {
  // Get assigned job list for freelancer
  getFeAssignedJobList: async () => {
    const response = await api.get('/getFeAssignedJobList');
    return response.data;
  },

  // Update job status
  updateJobStatus: async (cujId, jobStatus) => {
    const response = await api.put('/updateJobStatus', {
      cuj_id: btoa(cujId), // Encode to base64
      job_status: jobStatus
    });
    return response.data;
  },

  // Store freelancer comment
  storeFeComment: async (cujId, comment) => {
    const response = await api.post('/storeFeComment', {
      cuj_id: cujId, // Don't encode in request body, only in URL
      comment: comment
    });
    return response.data;
  },

  // Get job comments
  getJobComments: async (cujId, feUserId) => {
    const response = await api.get(`/getJobComments/${btoa(cujId)}/${btoa(feUserId)}`);
    return response.data;
  },

  // Get applied jobs list for freelancer
  getAppliedJobs: async () => {
    const response = await api.get('/getAppliedJobs');
    return response.data;
  },

  // Get applied job alerts (limited to 5 latest)
  getAppliedJobAlert: async () => {
    const response = await api.get('/getAppliedJobAlert');
    return response.data;
  },

  // Update freelancer availability status (online/offline)
  updateAvailability: async (userId, isOnline) => {
    const encodedUserId = btoa(userId.toString());
    const status = isOnline ? 1 : 0;
    const response = await api.get(`/FeAvailable/${status}/${encodedUserId}`);
    return response.data;
  }
};

// Message API functions
export const messageAPI = {
  // Get prefilled messages
  getPrefilledMessages: async () => {
    const response = await api.get('/messages1');
    return response.data;
  }
};

// Testimonial API functions
export const testimonialAPI = {
  // Get testimonials for a freelancer
  getFreelancerTestimonials: async (freelancerId) => {
    const encodedId = btoa(freelancerId.toString());
    const response = await api.get(`/getFeTestimonial/${encodedId}`);
    return response.data;
  },

  // Store a new testimonial
  storeTestimonial: async (testimonialData) => {
    const response = await api.post('/storetestimonial', {
      fp_u_id: testimonialData.freelancerId,
      testimonial_val: testimonialData.comment,
      rating_val: testimonialData.rating
    });
    return response.data;
  }
};

// Terms and Conditions API functions
export const termsAPI = {
  // Get terms and conditions
  getTermsAndConditions: async () => {
    const response = await api.get('/cms-pages1/MQ=='); // 'MQ==' is base64 for '1'
    return response.data;
  }
};

// User Notification API functions (for freelancers and customers)
export const userNotificationAPI = {
  // Get user notifications
  getNotifications: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    const response = await api.post(`/notifications/read/${notificationId}`);
    return response.data;
  }
};

// Contact Sharing API functions
export const contactShareAPI = {
  // Get contact share requests
  getContactRequests: async () => {
    const response = await api.get('/share-contact');
    return response.data;
  },

  // Send OTP for contact verification
  sendOtp: async (type, value) => {
    const response = await api.post('/sharecontactsend', {
      type: type,
      value: value
    });
    return response.data;
  },

  // Verify OTP
  verifyOtp: async (type, value, otp) => {
    const response = await api.post('/sharecontactverify', {
      type: type,
      value: value,
      otp: otp
    });
    return response.data;
  },

  // Resend OTP
  resendOtp: async (type, value) => {
    const response = await api.post('/sharecontactreset', {
      type: type,
      value: value
    });
    return response.data;
  },

  // Reply to contact request (share contact)
  replyToContactRequest: async (requestId, contactDetails) => {
    const encodedId = btoa(requestId.toString());
    const endpoint = `/share-contact/reply/${encodedId}`;
    const response = await api.post(endpoint, contactDetails);
    return response.data;
  }
};

// SEO API functions
export const seoAPI = {
  // Get SEO data for a page by slug ID
  getSEOData: async (slugId) => {
    try {
      const response = await api.get(`/getSEOData/${slugId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SEO data:', error);
      throw error;
    }
  }
};

export default api;